// Nova Poshta delivery picker used in the cart page and the cart drawer.
// Defined as a custom element so it re-initialises itself when the drawer
// re-renders its contents via innerHTML.

const NP_API_URL = 'https://api.novaposhta.ua/v2.0/json/';

async function npApiCall(apiKey, modelName, calledMethod, methodProperties = {}) {
  try {
    const res = await fetch(NP_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apiKey, modelName, calledMethod, methodProperties })
    });
    const json = await res.json();
    if (!json.success) {
      console.warn('NP API error:', json.errors);
      return [];
    }
    return json.data;
  } catch (e) {
    console.error('NP API call failed:', e);
    return [];
  }
}

function debounce(fn, ms = 300) {
  let timer = null;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), ms);
  };
}

const SPINNER_SVG =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
  '<path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">' +
  '<animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>' +
  '</path></svg>';

class NovaPoshtaWidget extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.apiKey = this.dataset.npApiKey;
    this.isDrawer = this.dataset.npIsDrawer === 'true';

    this.selectedCityRef = null;
    this.selectedSettlementRef = null;
    this.selectedCityName = null;
    this.deliveryType = 'warehouse';
    this.warehouseRef = '';
    this.streetRef = '';
    this.streetName = '';
    this.activeDropdown = null;
    this.activeIndex = -1;

    this.cityInput = this.$('[data-np-city-input]');
    this.cityDropdown = this.$('[data-np-city-dropdown]');
    this.warehouseInput = this.$('[data-np-warehouse-input]');
    this.warehouseDropdown = this.$('[data-np-warehouse-dropdown]');
    this.streetInput = this.$('[data-np-street-input]');
    this.streetDropdown = this.$('[data-np-street-dropdown]');
    this.houseInput = this.$('[data-np-house-input]');
    this.aptInput = this.$('[data-np-apartment-input]');

    this.bind();
    this.restoreSavedData();
    this.$$('.np-widget__input').forEach((input) => this.updateClearButton(input));
    this.syncCheckout();
  }

  $(sel) {
    return this.querySelector(sel);
  }

  $$(sel) {
    return this.querySelectorAll(sel);
  }

  bind() {
    this.addEventListener('keydown', (e) => this.onKeydown(e));

    this.outsideClick = (e) => {
      if (!this.contains(e.target)) {
        this.$$('.np-widget__dropdown').forEach((d) => (d.hidden = true));
        this.activeDropdown = null;
      }
    };
    document.addEventListener('click', this.outsideClick);

    this.$$('[data-np-radio]').forEach((radio) => {
      radio.addEventListener('change', () => {
        this.deliveryType = radio.value;
        this.$('[data-np-warehouse-section]').hidden = this.deliveryType !== 'warehouse';
        this.$('[data-np-courier-section]').hidden = this.deliveryType === 'warehouse';
        this.updateAttributes();
      });
    });

    this.$$('.np-widget__quick-city').forEach((btn) => {
      btn.addEventListener('click', () => this.pickQuickCity(btn.dataset.npCityName));
    });

    this.cityInput.addEventListener('input', debounce(() => this.searchCity(), 300));
    this.warehouseInput.addEventListener(
      'input',
      debounce(() => {
        this.warehouseRef = '';
        this.loadWarehouses(this.warehouseInput.value.trim());
      }, 300)
    );
    this.warehouseInput.addEventListener('focus', () => {
      if (this.selectedCityRef && !this.warehouseInput.value) this.loadWarehouses();
    });

    if (this.streetInput) {
      this.streetInput.addEventListener('input', debounce(() => this.searchStreet(), 300));
    }
    if (this.houseInput) {
      this.houseInput.addEventListener('input', debounce(() => this.updateAttributes(), 500));
    }
    if (this.aptInput) {
      this.aptInput.addEventListener('input', debounce(() => this.updateAttributes(), 500));
    }

    this.$$('[data-np-clear]').forEach((btn) => {
      const input = btn.parentElement.querySelector('.np-widget__input');
      if (!input) return;
      input.addEventListener('input', () => this.updateClearButton(input));
      btn.addEventListener('click', () => this.clearField(input, btn));
    });

    // The checkout button lives outside this element, so it is intercepted on
    // the shared cart container (the drawer, or the cart page form).
    this.scope = this.closest('cart-drawer') || this.closest('form') || document;
    this.checkoutClick = (e) => {
      const btn = e.target.closest('button[name="checkout"]');
      if (!btn || !this.scope.contains(btn) || !this.selectedCityName) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      btn.disabled = true;
      window.location.href = this.buildCheckoutUrl();
    };
    document.addEventListener('click', this.checkoutClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.outsideClick);
    document.removeEventListener('click', this.checkoutClick, true);
  }

  // --- Dropdown rendering ---

  showDropdownLoading(dropdownEl) {
    const loading = document.createElement('div');
    loading.className = 'np-widget__dropdown-loading';
    loading.innerHTML = SPINNER_SVG;
    loading.append(' ' + (this.dataset.npTextLoading || ''));

    dropdownEl.innerHTML = '';
    dropdownEl.appendChild(loading);
    this.activeIndex = -1;
    dropdownEl.hidden = false;
  }

  showDropdown(dropdownEl, items, onSelect) {
    dropdownEl.innerHTML = '';
    this.activeIndex = -1;

    if (!items.length) {
      const empty = document.createElement('div');
      empty.className = 'np-widget__dropdown-empty';
      empty.textContent = this.dataset.npTextEmpty || '';
      dropdownEl.appendChild(empty);
      dropdownEl.hidden = false;
      this.activeDropdown = { el: dropdownEl, items: [], onSelect };
      return;
    }

    items.forEach((item) => {
      const div = document.createElement('div');
      div.className = 'np-widget__dropdown-item';
      div.textContent = item.label;
      div.addEventListener('click', () => {
        onSelect(item);
        dropdownEl.hidden = true;
        this.activeDropdown = null;
      });
      dropdownEl.appendChild(div);
    });
    dropdownEl.hidden = false;
    this.activeDropdown = { el: dropdownEl, items, onSelect };
  }

  // The spinner and the clear button share the same corner of the field.
  showSpinner(selector, show) {
    const el = this.$(selector);
    if (!el) return;
    el.hidden = !show;

    const input = el.parentElement.querySelector('.np-widget__input');
    const clearBtn = el.parentElement.querySelector('[data-np-clear]');
    if (!clearBtn) return;
    if (show) {
      clearBtn.hidden = true;
    } else if (input) {
      this.updateClearButton(input);
    }
  }

  updateClearButton(input) {
    const clearBtn = input.parentElement.querySelector('[data-np-clear]');
    if (clearBtn) clearBtn.hidden = !input.value || input.disabled;
  }

  onKeydown(e) {
    if (!this.activeDropdown) return;
    const items = this.activeDropdown.el.querySelectorAll('.np-widget__dropdown-item');

    if (e.key === 'Escape') {
      this.activeDropdown.el.hidden = true;
      this.activeDropdown = null;
      return;
    }
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
    } else if (e.key === 'Enter' && this.activeIndex >= 0) {
      e.preventDefault();
      this.activeDropdown.onSelect(this.activeDropdown.items[this.activeIndex]);
      this.activeDropdown.el.hidden = true;
      this.activeDropdown = null;
      return;
    } else {
      return;
    }

    items.forEach((item, i) => {
      item.classList.toggle('np-widget__dropdown-item--active', i === this.activeIndex);
      if (i === this.activeIndex) item.scrollIntoView({ block: 'nearest' });
    });
  }

  // --- Search ---

  mapCities(npData) {
    return (npData?.[0]?.Addresses || []).map((c) => ({
      label: c.Present,
      cityRef: c.DeliveryCity,
      settlementRef: c.Ref,
      name: c.MainDescription
    }));
  }

  async searchCity() {
    const q = this.cityInput.value.trim();
    if (q.length < 2) {
      this.cityDropdown.hidden = true;
      return;
    }

    this.showSpinner('[data-np-city-spinner]', true);
    this.showDropdownLoading(this.cityDropdown);
    try {
      const data = await npApiCall(this.apiKey, 'Address', 'searchSettlements', {
        CityName: q,
        Limit: '20'
      });
      this.showDropdown(this.cityDropdown, this.mapCities(data), (city) => this.selectCity(city));
    } finally {
      this.showSpinner('[data-np-city-spinner]', false);
    }
  }

  async pickQuickCity(name) {
    this.cityInput.value = name;
    this.showSpinner('[data-np-city-spinner]', true);
    try {
      const data = await npApiCall(this.apiKey, 'Address', 'searchSettlements', {
        CityName: name,
        Limit: '5'
      });
      const cities = this.mapCities(data);
      if (cities.length) this.selectCity(cities[0]);
    } finally {
      this.showSpinner('[data-np-city-spinner]', false);
    }
  }

  selectCity(city) {
    this.cityInput.value = city.label;
    this.selectedCityRef = city.cityRef;
    this.selectedSettlementRef = city.settlementRef;
    this.selectedCityName = city.name;
    this.warehouseRef = '';
    this.streetRef = '';
    this.streetName = '';

    this.enableDependentInputs();
    if (this.warehouseInput) this.warehouseInput.value = '';
    if (this.streetInput) this.streetInput.value = '';

    this.$$('.np-widget__quick-city').forEach((btn) => {
      btn.classList.toggle('np-widget__quick-city--active', btn.dataset.npCityName === city.name);
    });

    this.$$('.np-widget__input').forEach((input) => this.updateClearButton(input));
    this.updateAttributes();
  }

  enableDependentInputs() {
    [this.warehouseInput, this.streetInput].forEach((input) => {
      if (input) input.disabled = false;
    });
  }

  async loadWarehouses(query = '') {
    if (!this.selectedCityRef) return;

    this.showSpinner('[data-np-warehouse-spinner]', true);
    this.showDropdownLoading(this.warehouseDropdown);
    try {
      const props = { CityRef: this.selectedCityRef, Limit: '50' };
      if (query) props.FindByString = query;

      const data = await npApiCall(this.apiKey, 'Address', 'getWarehouses', props);
      const warehouses = (data || []).map((w) => ({ label: w.Description, ref: w.Ref }));

      this.showDropdown(this.warehouseDropdown, warehouses, (wh) => {
        this.warehouseInput.value = wh.label;
        this.warehouseRef = wh.ref;
        this.updateClearButton(this.warehouseInput);
        this.updateAttributes();
      });
    } finally {
      this.showSpinner('[data-np-warehouse-spinner]', false);
    }
  }

  async searchStreet() {
    if (!this.selectedSettlementRef) return;
    const q = this.streetInput.value.trim();
    if (q.length < 2) {
      this.streetDropdown.hidden = true;
      return;
    }

    this.showSpinner('[data-np-street-spinner]', true);
    this.showDropdownLoading(this.streetDropdown);
    try {
      const data = await npApiCall(this.apiKey, 'Address', 'searchSettlementStreets', {
        SettlementRef: this.selectedSettlementRef,
        StreetName: q,
        Limit: '20'
      });
      const streets = (data?.[0]?.Addresses || []).map((s) => ({
        label: s.Present,
        ref: s.SettlementStreetRef,
        name: s.SettlementStreetDescription || s.Present
      }));

      this.showDropdown(this.streetDropdown, streets, (st) => {
        this.streetInput.value = st.label;
        this.streetRef = st.ref;
        this.streetName = st.name;
        this.updateClearButton(this.streetInput);
        this.updateAttributes();
      });
    } finally {
      this.showSpinner('[data-np-street-spinner]', false);
    }
  }

  clearField(input, btn) {
    if (input.hasAttribute('data-np-city-input')) {
      input.value = '';
      this.selectedCityRef = null;
      this.selectedSettlementRef = null;
      this.selectedCityName = null;
      this.warehouseRef = '';
      this.streetRef = '';
      this.streetName = '';
      this.cityDropdown.hidden = true;
      this.$$('.np-widget__quick-city').forEach((c) =>
        c.classList.remove('np-widget__quick-city--active')
      );
      [this.warehouseInput, this.streetInput].forEach((dependent) => {
        if (!dependent) return;
        dependent.value = '';
        dependent.disabled = true;
      });
      [this.houseInput, this.aptInput].forEach((dependent) => {
        if (dependent) dependent.value = '';
      });
      this.warehouseDropdown.hidden = true;
      if (this.streetDropdown) this.streetDropdown.hidden = true;
      this.$$('[data-np-clear]').forEach((c) => (c.hidden = true));
    } else if (input.hasAttribute('data-np-warehouse-input')) {
      input.value = '';
      this.warehouseRef = '';
      this.warehouseDropdown.hidden = true;
    } else if (input.hasAttribute('data-np-street-input')) {
      input.value = '';
      this.streetRef = '';
      this.streetName = '';
      if (this.streetDropdown) this.streetDropdown.hidden = true;
    }

    btn.hidden = true;
    input.focus();
    this.updateAttributes();
  }

  // --- Persistence ---

  updateAttributes() {
    const isWarehouse = this.deliveryType === 'warehouse';
    const attrs = {
      'Спосіб доставки': isWarehouse ? 'Нова Пошта - Відділення' : "Нова Пошта - Кур'єр",
      'Місто': this.selectedCityName || '',
      'Місто Ref': this.selectedCityRef || '',
      'Місто Settlement Ref': this.selectedSettlementRef || '',
      'Відділення': isWarehouse && this.warehouseInput ? this.warehouseInput.value : '',
      'Відділення Ref': isWarehouse ? this.warehouseRef || '' : '',
      'Вулиця': isWarehouse ? '' : this.streetName || '',
      'Вулиця Ref': isWarehouse ? '' : this.streetRef || '',
      'Будинок': isWarehouse || !this.houseInput ? '' : this.houseInput.value,
      'Квартира': isWarehouse || !this.aptInput ? '' : this.aptInput.value
    };

    // Persist across reloads, and mirror into the cart form's hidden inputs.
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch((e) => console.error('Failed to update cart attributes:', e));

    this.$$('[data-np-attr]').forEach((input) => {
      input.value = attrs[input.dataset.npAttr] || '';
    });

    this.showStatus();
    this.syncCheckout();
  }

  // A Nova Poshta order without a destination cannot be shipped, so checkout
  // stays blocked until the address is complete.
  isComplete() {
    if (!this.selectedCityName) return false;
    if (this.deliveryType === 'warehouse') return !!this.warehouseRef;
    return !!this.streetRef && !!(this.houseInput && this.houseInput.value.trim());
  }

  syncCheckout() {
    const ready = this.isComplete();
    const blocked = this.dataset.npTextRequired || '';

    this.scope.querySelectorAll('button[name="checkout"]').forEach((btn) => {
      // The original label is kept on the button, so it survives the toggle.
      if (!btn.dataset.npLabel) btn.dataset.npLabel = btn.textContent.trim();

      btn.disabled = !ready;
      btn.setAttribute('aria-disabled', String(!ready));
      btn.textContent = ready ? btn.dataset.npLabel : blocked;
    });
  }

  showStatus() {
    const statusEl = this.$('[data-np-status]');
    if (!statusEl) return;

    let msg = '';
    if (this.selectedCityName) {
      if (this.deliveryType === 'warehouse' && this.warehouseRef) {
        msg = this.selectedCityName + ', ' + this.warehouseInput.value;
      } else if (this.deliveryType === 'courier' && this.streetName) {
        msg = this.selectedCityName + ', ' + this.streetName;
        if (this.houseInput && this.houseInput.value) msg += ' ' + this.houseInput.value;
        if (this.aptInput && this.aptInput.value) msg += ', кв. ' + this.aptInput.value;
      }
    }

    statusEl.textContent = msg;
    statusEl.hidden = !msg;
  }

  restoreSavedData() {
    const d = this.dataset;
    if (d.npSavedMethod && d.npSavedMethod.includes("Кур'єр")) {
      this.deliveryType = 'courier';
      const courierRadio = this.querySelector('[data-np-radio][value="courier"]');
      if (courierRadio) courierRadio.checked = true;
      this.$('[data-np-warehouse-section]').hidden = true;
      this.$('[data-np-courier-section]').hidden = false;
    }

    if (!d.npSavedCity || !d.npSavedCityRef) return;

    this.cityInput.value = d.npSavedCity;
    this.selectedCityRef = d.npSavedCityRef;
    this.selectedSettlementRef = d.npSavedSettlementRef || d.npSavedCityRef;
    this.selectedCityName = d.npSavedCity;
    this.enableDependentInputs();

    this.$$('.np-widget__quick-city').forEach((btn) => {
      btn.classList.toggle('np-widget__quick-city--active', btn.dataset.npCityName === d.npSavedCity);
    });

    if (this.deliveryType === 'warehouse') {
      if (d.npSavedWarehouse && this.warehouseInput) this.warehouseInput.value = d.npSavedWarehouse;
      this.warehouseRef = d.npSavedWarehouseRef || '';
    } else {
      if (d.npSavedStreet && this.streetInput) {
        this.streetInput.value = d.npSavedStreet;
        this.streetRef = d.npSavedStreetRef || '';
        this.streetName = d.npSavedStreet;
      }
      if (d.npSavedHouse && this.houseInput) this.houseInput.value = d.npSavedHouse;
      if (d.npSavedApartment && this.aptInput) this.aptInput.value = d.npSavedApartment;
    }

    this.showStatus();
  }

  // Nova Poshta addresses are not real street addresses, so the checkout is
  // prefilled through a permalink instead of the normal cart submit.
  buildCheckoutUrl() {
    const params = new URLSearchParams();
    params.append('checkout[shipping_address][city]', this.selectedCityName);
    params.append('checkout[shipping_address][country]', 'Ukraine');

    if (this.deliveryType === 'warehouse') {
      if (this.warehouseInput && this.warehouseInput.value) {
        params.append('checkout[shipping_address][address1]', this.warehouseInput.value);
      }
    } else {
      const parts = [];
      if (this.streetName) parts.push(this.streetName);
      if (this.houseInput && this.houseInput.value) parts.push('буд. ' + this.houseInput.value);
      if (this.aptInput && this.aptInput.value) parts.push('кв. ' + this.aptInput.value);
      if (parts.length) params.append('checkout[shipping_address][address1]', parts.join(', '));
    }

    params.append('checkout[shipping_address][zip]', '00000');
    return '/checkout?' + params.toString();
  }
}

if (!customElements.get('nova-poshta-widget')) {
  customElements.define('nova-poshta-widget', NovaPoshtaWidget);
}
